import { GeneralUtils } from "asset-analysis";
import { TiArrowUpOutline, TiArrowDownOutline } from "react-icons/ti";
import { parseInvestorTypeSimple } from "../../../../utils";
import Skeleton from "react-loading-skeleton";
import React, { useEffect } from "react";
import { isMobile } from "react-device-detect";
import { LuSettings2 } from "react-icons/lu";
import useAnalysisTypePreferences from "../../../../context/AnalysisTypePreferencesContext";
import { useTour } from "@reactour/tour";
import AnalysisTypeSelectorDialog from "../../AnalysisTypeSelectorDialog";

/**
 *  Card containing all the analysis that is quick and easy to read for the user,
 */
export function SummaryOverviewCard({
  indications,
  investorType,
  preferences,
  report,
}) {
  const { setIsOpen: setIsOpenPreferences, openTour } =
    useAnalysisTypePreferences();
  const { setCurrentStep, setIsOpen } = useTour();

  useEffect(() => {
    if (openTour && !!indications) {
      document.body.classList.add("body-overflow");
      setTimeout(() => {
        setCurrentStep(0);
        setIsOpen(true);
      }, 1500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openTour, indications]);

  if (!indications) {
    return skeletonLoading();
  }

  //Get all indications numbers
  const overviewResults = GeneralUtils.getGeneralOverview(
    indications,
    preferences
  );

  //Get an array containing the BUY and SELL points, the first value is a indication that have most points (BUY OR SELL).
  const orderedSummaryInd = getOrderedSummaryIndications(
    overviewResults.generalSummary
  );

  function LucyRecomendation() {
    const alert =
      "A análise acima reflete apenas a opinião da nossa inteligência artificial sobre o ativo, não é uma indicação direta de compra ou venda.";
    const aiSentimentKeyDictionary = [
      {
        action: "Espera por melhores oportunidades",
        color: "#BDA856",
      },
      {
        action: "Compra",
        color: "#538E57",
      },
      {
        action: "Venda",
        color: "#AC5C5D",
      },
    ];

    if (report) {
      const aiSentiment = aiSentimentKeyDictionary[report.ai_sentiment];

      return (
        <>
          <p className="mg-t-15 tx-12">
            Segundo análise feita por nossa <b>I.A</b> com base no seu perfil de
            investidor atual (<b>{parseInvestorTypeSimple(investorType)}</b>), a
            posição atual da
            <b> Lucy</b> é de{" "}
            <b style={{ color: aiSentiment.color }}>{aiSentiment.action}.</b>
          </p>
          <p className="tx-10 text-center" style={{ color: "gray" }}>
            {alert}
          </p>
        </>
      );
    }

    return (
      <>
        <p className="mg-t-15 tx-12">
          Nossa <b>I.A</b> está organizando dados com base no seu perfil de
          investidor atual (<b>{parseInvestorTypeSimple(investorType)}</b>), em
          breve você conseguirá visualizar aqui um resumo da opinião da Lucy.
        </p>
        <p className="tx-10 text-center" style={{ color: "gray" }}>
          {alert}
        </p>
      </>
    );
  }

  return (
    <div
      className="card card-minimal-one"
      id={!isMobile ? "analysis-step-7" : ""}
    >
      <div className="card-body animated fadeInDown">
        <label style={{ color: "var(--primary-blue)" }}>
          Análise rápida
          <button
            type="button"
            className="unstyled-btn settings-btn"
            onClick={() => setIsOpenPreferences(true)}
            id="analysis-type-preferences"
          >
            <LuSettings2 size={18} />
          </button>
        </label>
        {
          //Bigger indications
          <h6 id={isMobile ? "analysis-step-7" : ""}>
            <label>
              <span style={{ color: orderedSummaryInd[0].color }}>
                {orderedSummaryInd[0].icon}
                {orderedSummaryInd[0].value}
              </span>
              {orderedSummaryInd[0].name}
            </label>
          </h6>
        }
        {
          //Less Indications
          <p className="tx-18" style={{ marginLeft: 7 }}>
            <label>
              <span style={{ color: orderedSummaryInd[1].color }}>
                {orderedSummaryInd[1].icon} {orderedSummaryInd[1].value}{" "}
              </span>
              {orderedSummaryInd[1].name}
            </label>
          </p>
        }

        <div>
          {
            //Results by category
            overviewResults.byCategory.map((item, i) => {
              return (
                <p className="invoice-info-row" key={i}>
                  <span>{item.name}</span>

                  {item.qntBuy === -1 && item.qntSell === -1 ? (
                    <span style={{ color: "var(--text-7)", fontWeight: 500 }}>
                      OFF
                    </span>
                  ) : (
                    <span style={{ color: "#4caf50" }}>
                      {
                        //Strong font if it has bigger than the sell
                        item.qntBuy > item.qntSell ? (
                          <b>{item.qntBuy}</b>
                        ) : (
                          item.qntBuy
                        )
                      }
                      <span style={{ color: "#000000" }}>{" | "}</span>
                      {
                        //Strong font if it has big than buy
                        <span style={{ color: "#f44334" }}>
                          {item.qntSell > item.qntBuy ? (
                            <b>{item.qntSell}</b>
                          ) : (
                            item.qntSell
                          )}
                        </span>
                      }
                    </span>
                  )}
                </p>
              );
            })
          }

          <LucyRecomendation />
        </div>
      </div>
      <AnalysisTypeSelectorDialog investorType={investorType} />
    </div>
  );
}

export const SummaryOverviewCardMemo = React.memo(SummaryOverviewCard);

/**
 * Organize the Bigger summary items by more points, push to an array organized.
 */
const getOrderedSummaryIndications = (summary) => {
  let response = [];
  let summaryItems = {
    buy: {
      name: "Fatores de compra",
      value: summary.qntBuy,
      color: "#66bb6a",
      icon: <TiArrowUpOutline />,
    },
    sell: {
      name: "Fatores de venda",
      value: summary.qntSell,
      color: "#ef5350",
      icon: <TiArrowDownOutline />,
    },
  };
  //Organize by most indications...
  if (summary.qntBuy >= summary.qntSell) {
    response = [summaryItems.buy, summaryItems.sell];
  } else {
    response = [summaryItems.sell, summaryItems.buy];
  }
  return response;
};

const skeletonLoading = () => {
  return (
    <div className="card card-minimal-one animated fadeInDown">
      <div className="card-body">
        <label>Análise Rápida</label>
        <h5 style={{ marginTop: 10 }}>
          <label>
            <span style={{ color: "#689f38" }}>
              <TiArrowUpOutline />
              <Skeleton style={{ marginLeft: 10 }} width={180} height={20} />
            </span>
          </label>
        </h5>

        <h5 style={{ marginTop: 10 }}>
          <label>
            <span style={{ color: "#f44334" }}>
              <TiArrowDownOutline />
              <Skeleton style={{ marginLeft: 10 }} width={120} height={20} />
            </span>
          </label>
        </h5>
        <div>
          <p className="invoice-info-row">
            <span>Análise técnica</span>

            <span>
              <Skeleton width={30} />
            </span>
          </p>

          <p className="invoice-info-row">
            <span>Análise fundamentalista</span>

            <span>
              <Skeleton width={30} />
            </span>
          </p>

          <p className="invoice-info-row">
            <span>Avaliações importantes</span>
            <span>
              <Skeleton width={30} />
            </span>
          </p>

          <div className="mg-t-20">
            <center>
              <Skeleton width={"80%"} count={3} />
            </center>
          </div>
        </div>
      </div>
    </div>
  );
};
